/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { remoteSessionClientsAttachKeySet } from "../funcs/remoteSessionClientsAttachKeySet.js";
import { remoteSessionClientsAttachUserSessionIssuer } from "../funcs/remoteSessionClientsAttachUserSessionIssuer.js";
import { remoteSessionClientsCreate } from "../funcs/remoteSessionClientsCreate.js";
import { remoteSessionClientsCreateCimd } from "../funcs/remoteSessionClientsCreateCimd.js";
import { remoteSessionClientsDelete } from "../funcs/remoteSessionClientsDelete.js";
import { remoteSessionClientsDetachKeySet } from "../funcs/remoteSessionClientsDetachKeySet.js";
import { remoteSessionClientsDetachUserSessionIssuer } from "../funcs/remoteSessionClientsDetachUserSessionIssuer.js";
import { remoteSessionClientsGet } from "../funcs/remoteSessionClientsGet.js";
import { remoteSessionClientsGetNewClientCallbackUrl } from "../funcs/remoteSessionClientsGetNewClientCallbackUrl.js";
import { remoteSessionClientsList } from "../funcs/remoteSessionClientsList.js";
import { remoteSessionClientsPrepareEMA } from "../funcs/remoteSessionClientsPrepareEMA.js";
import { remoteSessionClientsReadEMA } from "../funcs/remoteSessionClientsReadEMA.js";
import { remoteSessionClientsUnlinkEMA } from "../funcs/remoteSessionClientsUnlinkEMA.js";
import { remoteSessionClientsUpdate } from "../funcs/remoteSessionClientsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { IdentityChainingPreparation } from "../models/components/identitychainingpreparation.js";
import { NewClientCallbackURLResult } from "../models/components/newclientcallbackurlresult.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import {
  AttachRemoteSessionClientKeySetRequest,
  AttachRemoteSessionClientKeySetSecurity,
} from "../models/operations/attachremotesessionclientkeyset.js";
import {
  AttachUserSessionIssuerRequest,
  AttachUserSessionIssuerSecurity,
} from "../models/operations/attachusersessionissuer.js";
import {
  CreateCimdRemoteSessionClientRequest,
  CreateCimdRemoteSessionClientSecurity,
} from "../models/operations/createcimdremotesessionclient.js";
import {
  CreateRemoteSessionClientRequest,
  CreateRemoteSessionClientSecurity,
} from "../models/operations/createremotesessionclient.js";
import {
  DeleteRemoteSessionClientRequest,
  DeleteRemoteSessionClientSecurity,
} from "../models/operations/deleteremotesessionclient.js";
import {
  DetachRemoteSessionClientKeySetRequest,
  DetachRemoteSessionClientKeySetSecurity,
} from "../models/operations/detachremotesessionclientkeyset.js";
import {
  DetachUserSessionIssuerRequest,
  DetachUserSessionIssuerSecurity,
} from "../models/operations/detachusersessionissuer.js";
import {
  GetNewRemoteSessionClientCallbackUrlRequest,
  GetNewRemoteSessionClientCallbackUrlSecurity,
} from "../models/operations/getnewremotesessionclientcallbackurl.js";
import {
  GetRemoteSessionClientRequest,
  GetRemoteSessionClientSecurity,
} from "../models/operations/getremotesessionclient.js";
import {
  ListRemoteSessionClientsRequest,
  ListRemoteSessionClientsResponse,
  ListRemoteSessionClientsSecurity,
} from "../models/operations/listremotesessionclients.js";
import {
  PrepareEMARequest,
  PrepareEMASecurity,
} from "../models/operations/prepareema.js";
import {
  ReadEMARequest,
  ReadEMASecurity,
} from "../models/operations/readema.js";
import {
  UnlinkEMARequest,
  UnlinkEMASecurity,
} from "../models/operations/unlinkema.js";
import {
  UpdateRemoteSessionClientRequest,
  UpdateRemoteSessionClientSecurity,
} from "../models/operations/updateremotesessionclient.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class RemoteSessionClients extends ClientSDK {
  /**
   * attachKeySet remoteSessionClients
   *
   * @remarks
   * Attach an organization JSON Web Key Set to a remote_session_client, opting it into signing private_key_jwt assertions. The set must belong to the client's organization. Requires the customer_managed_encryption_keys entitlement.
   */
  async attachKeySet(
    request: AttachRemoteSessionClientKeySetRequest,
    security?: AttachRemoteSessionClientKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsAttachKeySet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * attachUserSessionIssuer remoteSessionClients
   *
   * @remarks
   * Attach a user_session_issuer to a remote_session_client by recording the binding in the join table. Rejected when another client is already bound to the same user_session_issuer for this client's remote_session_issuer.
   */
  async attachUserSessionIssuer(
    request: AttachUserSessionIssuerRequest,
    security?: AttachUserSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsAttachUserSessionIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createRemoteSessionClient remoteSessionClients
   *
   * @remarks
   * Register a remote_session_client by supplying a client_id and optional client_secret obtained out-of-band from the upstream issuer.
   */
  async create(
    request: CreateRemoteSessionClientRequest,
    security?: CreateRemoteSessionClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createCimd remoteSessionClients
   *
   * @remarks
   * Register a remote_session_client in Client ID Metadata Document (CIMD) mode. Speakeasy generates the client_id (the URL of a hosted client metadata document) and serves the document publicly; the client carries no secret and authenticates with token_endpoint_auth_method=none. The owning issuer must advertise client_id_metadata_document_supported.
   */
  async createCimd(
    request: CreateCimdRemoteSessionClientRequest,
    security?: CreateCimdRemoteSessionClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsCreateCimd(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteRemoteSessionClient remoteSessionClients
   *
   * @remarks
   * Soft-delete a remote_session_client. Cascades to remote_sessions rows pointing at this client; affected principals are forced to re-authenticate.
   */
  async delete(
    request: DeleteRemoteSessionClientRequest,
    security?: DeleteRemoteSessionClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(remoteSessionClientsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * detachKeySet remoteSessionClients
   *
   * @remarks
   * Detach the JSON Web Key Set from a remote_session_client. Refused while the client declares token_endpoint_auth_method=private_key_jwt. A no-op when no set is attached. Requires the customer_managed_encryption_keys entitlement.
   */
  async detachKeySet(
    request: DetachRemoteSessionClientKeySetRequest,
    security?: DetachRemoteSessionClientKeySetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsDetachKeySet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * detachUserSessionIssuer remoteSessionClients
   *
   * @remarks
   * Detach a user_session_issuer from a remote_session_client by removing the binding from the join table. A no-op when the binding does not exist.
   */
  async detachUserSessionIssuer(
    request: DetachUserSessionIssuerRequest,
    security?: DetachUserSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsDetachUserSessionIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getRemoteSessionClient remoteSessionClients
   *
   * @remarks
   * Get a remote_session_client by id.
   */
  async get(
    request: GetRemoteSessionClientRequest,
    security?: GetRemoteSessionClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRemoteSessionClients remoteSessionClients
   *
   * @remarks
   * List remote_session_clients in the caller's project.
   */
  async list(
    request?: ListRemoteSessionClientsRequest | undefined,
    security?: ListRemoteSessionClientsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<
    PageIterator<ListRemoteSessionClientsResponse, { cursor: string }>
  > {
    return unwrapResultIterator(remoteSessionClientsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getNewClientCallbackUrl remoteSessionClients
   *
   * @remarks
   * Get the redirect URI a remote_session_client created now in the caller's project registers with its upstream provider.
   */
  async getNewClientCallbackUrl(
    request?: GetNewRemoteSessionClientCallbackUrlRequest | undefined,
    security?: GetNewRemoteSessionClientCallbackUrlSecurity | undefined,
    options?: RequestOptions,
  ): Promise<NewClientCallbackURLResult> {
    return unwrapAsync(remoteSessionClientsGetNewClientCallbackUrl(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * prepareEMA remoteSessionClients
   *
   * @remarks
   * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
   */
  async prepareEMA(
    request: PrepareEMARequest,
    security?: PrepareEMASecurity | undefined,
    options?: RequestOptions,
  ): Promise<IdentityChainingPreparation> {
    return unwrapAsync(remoteSessionClientsPrepareEMA(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * readEMA remoteSessionClients
   *
   * @remarks
   * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
   */
  async readEMA(
    request: ReadEMARequest,
    security?: ReadEMASecurity | undefined,
    options?: RequestOptions,
  ): Promise<IdentityChainingPreparation> {
    return unwrapAsync(remoteSessionClientsReadEMA(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * unlinkEMA remoteSessionClients
   *
   * @remarks
   * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
   */
  async unlinkEMA(
    request: UnlinkEMARequest,
    security?: UnlinkEMASecurity | undefined,
    options?: RequestOptions,
  ): Promise<IdentityChainingPreparation> {
    return unwrapAsync(remoteSessionClientsUnlinkEMA(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateRemoteSessionClient remoteSessionClients
   *
   * @remarks
   * Rotate the client_secret or change the non-issuer settings on an existing remote_session_client. Issuer attachments are managed via attachUserSessionIssuer / detachUserSessionIssuer.
   */
  async update(
    request: UpdateRemoteSessionClientRequest,
    security?: UpdateRemoteSessionClientSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RemoteSessionClient> {
    return unwrapAsync(remoteSessionClientsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
